<template>
  <v-container fluid>
    <template>
      <v-row dense>
        <v-col cols="6" sm="2">
          <h3>Quick Look</h3>
          <p class="mb-0">Horizon: {{ horizon }} {{ plur("year", horizon) }}</p>
          <p v-if="date" class="text-caption">{{ date }}</p>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="6" sm="2">
          <v-select
            dense
            v-model="selectedYear"
            :items="simulationYearArray"
            item-text="text"
            item-value="value"
            outlined
            label="Select Year"
            hide-details
          ></v-select>
        </v-col>
        <v-col v-if="selectedTab === 'nodal'" cols="6" sm="2">
          <v-autocomplete
            dense
            v-model="computedSelectedNodeId.nodeId"
            :items="nodes"
            item-text="name"
            item-value="id"
            outlined
            label="Select Node"
            hide-details
          ></v-autocomplete>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col v-if="firstYearTable" sm="12" md="4">
          <TableSimple :data="firstYearTable"></TableSimple>
        </v-col>
        <v-col v-if="lastYearTable" sm="12" md="4">
          <TableSimple :data="lastYearTable"></TableSimple>
        </v-col>
        <v-col v-if="selectedYearTable" sm="12" md="4">
          <TableSimple :data="selectedYearTable"></TableSimple>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { DateTime } from "luxon";
import { camelToTitleCase, formatNumber } from "@/plugins/utils";
import plur from "plur";

export default {
  name: "NodalPCQuickLook",
  components: {
    TableSimple: () => import("@/components/visualizations/TableSimple.vue"),
  },
  mixins: [],
  props: {
    selectedTab: { type: String, required: true, default: "nodal" },
    caseOutputs: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    inputCase: { type: Object, required: false, default: () => ({}) },
    horizontal: { type: Boolean, required: false, default: false },
    selectedNodeId: { type: Object, required: true, default: () => ({ nodeId: 1 }) },
  },
  data() {
    return {
      selectedItem: "",
      tab: 0,
      selectedYear: Object.keys(this.caseOutputs.yearlyResults)[0],
    };
  },
  computed: {
    caseInputs() {
      return this.inputCase.input;
    },

    computedSelectedNodeId: {
      get() {
        return this.selectedNodeId;
      },
      set(val) {
        this.$emit("update:selectedNodeId", val);
        this.$emit("change");
      },
    },
    nodes() {
      return this.inputCase.input.nodes.map((el) => ({
        name: el.name,
        id: el.id,
      }));
    },
    yearlyOutputsByNode() {
      return Object.keys(this.yearlyOutputs).reduce((acc, year) => {
        acc[year] = {
          ...Object.keys(this.yearlyOutputs[year]).reduce((acc2, res) => {
            if (!acc2[res]) {
              // eslint-disable-next-line no-param-reassign
              acc2[res] = [];
            }
            acc2[res].push(
              ...this.yearlyOutputs[year][res].filter(
                (el) => el.nodeID === this.selectedNodeId.nodeId
              )
            );
            return acc2;
          }, {}),
        };
        return acc;
      }, {});
    },
    resourcesWithOutput() {
      return Object.keys(Object.values(this.yearlyOutputs)[0]).reduce((acc, el) => {
        if (Object.values(this.yearlyOutputs)[0][el].length) {
          acc.push(el);
        }
        return acc;
      }, []);
    },
    resourceTitles() {
      return this.resourcesWithOutput.map((el) => camelToTitleCase(el));
    },
    firstYearTable() {
      if (!this.yearlyValues.length) return null;
      const firstYear = this.yearlyValues[0];
      return [
        {
          header: [`First Year (${firstYear.year})`, ""],
          rows: firstYear.values.map((row) => [row.title, `${row.value} ${row.suffix}`]),
        },
      ];
    },
    lastYearTable() {
      if (!this.yearlyValues.length) return null;
      const lastYear = this.yearlyValues[this.yearlyValues.length - 1];
      return [
        {
          header: [`Last Year (${lastYear.year})`, ""],
          rows: lastYear.values.map((row) => [row.title, `${row.value} ${row.suffix}`]),
        },
      ];
    },
    selectedYearTable() {
      if (!this.selectedYear) return null;
      const yearData = this.yearlyValues.find((el) => el.year === this.selectedYear);
      return [
        {
          header: [`Selected Year: ${yearData.year}`, ""],
          rows: yearData.values.map((row) => [row.title, `${row.value} ${row.suffix}`]),
        },
      ];
    },
    yearlyOutputs() {
      return this.caseOutputs.yearlyResults;
    },
    horizon() {
      return Object.keys(this.yearlyOutputs).length;
    },
    simulationYearArray() {
      return Object.keys(this.yearlyOutputs);
    },
    mappingNodal() {
      return [
        {
          title: "Demand",
          format: 0,
          item: { resource: "demands", key: "demand" },
          suffix: "MWh",
        },
        {
          title: "Thermal Generation",
          format: 0,
          item: { resource: "thermalGenerators", key: "generation" },
          suffix: "MWh",
        },
        {
          title: "Hydro Generation",
          format: 0,
          item: { resource: "hydroGenerators", key: "generation" },
          suffix: "MWh",
        },
        {
          title: "Renewable Generation",
          format: 0,
          item: { resource: "renewableGenerators", key: "generation" },
          suffix: "MWh",
        },
      ];
    },
    // This property describes what to show inside the quick look
    // when displaying systemic data.
    mappingSystemLevel() {
      return [
        {
          title: "Energy Demand",
          format: 0,
          item: { resource: "system", key: "energyDemand" },
          suffix: "GWh",
        },
        {
          title: "Peak Load",
          format: 0,
          item: { resource: "system", key: "peakLoad" },
          suffix: "MW",
        },
        {
          title: "Storage Capacity",
          format: 0,
          item: { resource: "storages", key: "assetCapacity" },
          suffix: "MW",
        },
        {
          title: "Thermal Generation",
          format: 0,
          item: { resource: "thermalGenerators", key: "generation" },
          suffix: "MW",
        },
        {
          title: "Hydro Generation",
          format: 0,
          item: { resource: "hydroGenerators", key: "generation" },
          suffix: "MW",
        },
        {
          title: "Renewable Generation",
          format: 0,
          item: { resource: "renewableGenerators", key: "generation" },
          suffix: "MW",
        },
        {
          title: "Emission Cost",
          format: 0,
          item: { resource: "system", key: "totalEmissionCost" },
          suffix: "$/tonCO2",
        },
      ];
    },
    yearlyValues() {
      if (this.selectedTab === "nodal") {
        return this.simulationYearArray.map((year) => ({
          year,
          values: this.mappingNodal.map((el) => ({
            title: el.title,
            suffix: el.suffix,
            totalValue: this.yearlyOutputsByNode[year][el.item.resource],
            value: this.yearlyOutputsByNode[year][el.item.resource]
              ? formatNumber(
                  Object.values(this.yearlyOutputsByNode[year][el.item.resource]).reduce((a, e) => {
                    return a + e[el.item.key];
                  }, 0),
                  el.format
                )
              : 0,
          })),
        }));
      }
      // If system, we'll aggregate all values
      return this.simulationYearArray.map((year) => ({
        year,
        values: this.mappingSystemLevel.map((el) => ({
          title: el.title,
          suffix: el.suffix,
          totalValue: this.yearlyOutputs[year][el.item.resource],
          value: formatNumber(
            this.yearlyOutputs[year][el.item.resource].reduce(
              (acc, asset) => acc + asset[el.item.key],
              0
            ),
            el.format
          ),
        })),
      }));
    },
    date() {
      return DateTime.fromISO(this.inputCase.created).toLocaleString(DateTime.DATETIME_MED);
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    plur,
    formatNumber,
  },
};
</script>

<style></style>
